import { Clock, AlertTriangle, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * TrialBanner — shows trial countdown for users on the 30-day full-access trial,
 * or an expired-state CTA. Hidden on paid tiers and for super-admins.
 */
export default function TrialBanner({ onUpgrade }) {
  const { user } = useAuth();
  if (!user) return null;
  // Hide for paid users (stored_tier !== "free")
  if ((user.stored_tier || user.tier) !== "free") return null;
  const days = user.trial_days_left;

  // Expired
  if (user.trial_active === false || days === 0) {
    return (
      <div
        data-testid="trial-expired-banner"
        className="bg-[#FFEBEE] border border-[#FFCDD2] rounded-sm p-4 mb-6 flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-[#C62828] shrink-0 mt-0.5" />
          <div>
            <h3 className="font-display font-extrabold text-base text-[#C62828] leading-tight">
              Your free trial has ended.
            </h3>
            <p className="text-xs text-[#C62828]/80 mt-1 leading-relaxed">
              Upgrade to continue enriching leads — or contact us to request a trial extension.
            </p>
          </div>
        </div>
        <button
          data-testid="trial-expired-cta"
          onClick={onUpgrade}
          className="shrink-0 bg-[#C62828] hover:bg-[#A11414] text-white font-medium text-xs uppercase tracking-widestx px-4 py-2.5 rounded-sm"
        >
          See plans
        </button>
      </div>
    );
  }

  // Active — countdown (full access trial)
  if (days != null) {
    const urgent = days <= 7;
    return (
      <div
        data-testid="trial-countdown-banner"
        className={`rounded-sm p-3 mb-6 flex items-center justify-between gap-4 ${
          urgent ? "bg-[#FFF8E1] border border-[#FFECB3]" : "bg-ink-50 border border-ink-100"
        }`}
      >
        <div className="flex items-center gap-2.5">
          {urgent ? (
            <Clock className="w-4 h-4 text-[#F57F17]" />
          ) : (
            <Sparkles className="w-4 h-4 text-brand" />
          )}
          <div className="text-xs">
            <span className={`font-semibold ${urgent ? "text-[#F57F17]" : "text-ink-700"}`}>
              {days} day{days === 1 ? "" : "s"} left in your free trial
            </span>
            <span className={`ml-2 ${urgent ? "text-[#F57F17]/80" : "text-ink-400"}`}>
              · full Apex access · 25 enrichments / mo · upgrade for more
            </span>
          </div>
        </div>
        <button
          data-testid="trial-upgrade-cta"
          onClick={onUpgrade}
          className={`shrink-0 text-[10px] uppercase tracking-widestx font-semibold px-3 py-1.5 rounded-sm transition-colors ${
            urgent
              ? "bg-[#F57F17] hover:bg-[#E65100] text-white"
              : "bg-white border border-ink-200 text-ink-700 hover:bg-ink-100"
          }`}
        >
          See plans
        </button>
      </div>
    );
  }
  return null;
}
